import { Action, FrameStencil, MarkerPlayer, StencilType } from "../../../types";
import { sliceItemUpdate } from "../../splice-item";
import { findClosestStencil } from "../find-closest";
import { getBallActions, getPossessionActions, isBallAction } from "../frame-stencil-filters";

interface CalculateNewActionTimesProps {
  action: Action;
  newAction: Action;
}

const calculateNewActionTimes = ({ action, newAction }: CalculateNewActionTimesProps) => {
  const { startTime, endTime } = action;
  const duration = endTime - startTime;

  const newStartTime = Math.max(startTime, newAction.endTime);
  const newEndTime = newStartTime + duration;

  return { ...action, startTime: newStartTime, endTime: newEndTime };
};

export interface CheckActionEndPointProps<T extends Action | MarkerPlayer> {
  action: Action;
  actionsToCheck: T[];
  stencils: FrameStencil[];
}

export const checkActionEndPoint = <T extends Action | MarkerPlayer>({
  action,
  actionsToCheck,
  stencils,
}: CheckActionEndPointProps<T>): { next: T | null; newStencils: FrameStencil[] } => {
  /** get all playerActions and possessionActions that aren't on another action's next
   * find closest to endPoint */
  const closestActionToEndPoint = findClosestStencil({
    target: { id: action.id, point: action.endPos },
    stencils: actionsToCheck,
  });

  const stencilsWithUpdatedNextAction = ((): FrameStencil[] => {
    if (!closestActionToEndPoint) return stencils;

    switch (closestActionToEndPoint.stencilType) {
      case StencilType.ACTION:
        return sliceItemUpdate({
          arr: stencils,
          item: calculateNewActionTimes({ action: closestActionToEndPoint, newAction: action }),
        });

      case StencilType.MARKER: {
        if (isBallAction(action)) {
          const playersNextPass = getBallActions(stencils).find(
            (pass) => pass.prevPlayer === closestActionToEndPoint.id
          );
          if (playersNextPass) {
            return sliceItemUpdate({
              arr: stencils,
              item: calculateNewActionTimes({ action: playersNextPass, newAction: action }),
            });
          }

          const playersNextPossession = getPossessionActions(stencils).find((s) =>
            closestActionToEndPoint.movements.map((m) => m.id).includes(s.id)
          );

          if (playersNextPossession) {
            return sliceItemUpdate({
              arr: stencils,
              item: calculateNewActionTimes({ action: playersNextPossession, newAction: action }),
            });
          }

          return stencils;
        } else return stencils;
      }
    }
  })();

  return { next: closestActionToEndPoint, newStencils: stencilsWithUpdatedNextAction };
};
